/**
 * F6 (Jun-2026, User-Wunsch) — Buchhaltung-Tab „Provisionen".
 *
 * Verwaltet/bestätigt die Verkäufer-Provisionen (Fahrzeug-Provision + Provision
 * auf Zusatzertrag). Filialleiter + Buchhaltung haken je Zeile ab; sobald beide
 * gesetzt sind, ist die Zeile abrufbar. Auszahlungs-Status pro Vorgang setzbar.
 */
import { useState, useEffect, useCallback } from "react";
import { Percent, Loader2, CheckCircle2, FileText, Users } from "lucide-react";
import { toast } from "sonner";
import api, { formatPrice } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";

function _curMonth() {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}

const PAYOUT_LABELS = {
    offen: "Offen",
    angefordert: "Angefordert",
    abgerechnet: "Abgerechnet",
    ausgezahlt: "Ausgezahlt",
};
const PAYOUT_CLS = {
    offen: "bg-gray-100 text-gray-700",
    angefordert: "bg-amber-100 text-amber-800",
    abgerechnet: "bg-blue-100 text-blue-800",
    ausgezahlt: "bg-emerald-100 text-emerald-800",
};

function Kpi({ label, value, cls = "text-[#111827]", testid }) {
    return (
        <div className="bg-white border border-gray-200 rounded-sm px-4 py-3" data-testid={testid}>
            <div className="text-[10px] font-bold uppercase tracking-wider text-gray-500">{label}</div>
            <div className={`text-xl font-black font-mono-num ${cls}`}>{value}</div>
        </div>
    );
}

export default function SellerCommissionsTab() {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [busy, setBusy] = useState("");
    const [month, setMonth] = useState(_curMonth());
    const [seller, setSeller] = useState("");
    const [pdfBusy, setPdfBusy] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const r = await api.get("/dealer/seller-commissions");
            setData(r.data);
        } catch {
            toast.error("Provisionen konnten nicht geladen werden");
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { load(); }, [load]);

    const toggleConf = async (lid, line, role, val) => {
        setBusy(`${lid}-${line}-${role}`);
        try {
            await api.patch(`/listings/${lid}/dispo/seller-commission`, { line, role, confirmed: val });
            await load();
        } catch {
            toast.error("Bestätigung fehlgeschlagen");
        } finally {
            setBusy("");
        }
    };

    const setPayout = async (lid, status) => {
        setBusy(`${lid}-payout`);
        try {
            await api.patch(`/dealer/seller-commissions/${lid}/payout`, { payout_status: status });
            toast.success(`Status: ${PAYOUT_LABELS[status]}`);
            await load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Status konnte nicht gesetzt werden");
        } finally {
            setBusy("");
        }
    };

    if (loading) {
        return <div className="py-12 text-center text-gray-500" data-testid="commissions-loading"><Loader2 className="w-5 h-5 animate-spin inline mr-2" /> Lädt …</div>;
    }
    const items = data?.items || [];
    const totals = data?.totals || {};
    const sellers = Array.from(
        new Map(items.filter((i) => i.seller_id).map((i) => [i.seller_id, i.seller_name || i.seller_id])).entries()
    ).map(([id, name]) => ({ id, name }));

    const downloadAbrechnung = async () => {
        if (!seller) { toast.error("Bitte Verkäufer wählen"); return; }
        setPdfBusy(true);
        try {
            const res = await api.get(`/dealer/seller-commissions/${seller}/abrechnung.pdf`, {
                params: { month }, responseType: "blob",
            });
            const url = window.URL.createObjectURL(res.data);
            const a = document.createElement("a");
            a.href = url;
            a.download = `provisionsabrechnung_${seller}_${month}.pdf`;
            document.body.appendChild(a); a.click(); a.remove();
            window.URL.revokeObjectURL(url);
        } catch {
            toast.error("Abrechnung konnte nicht erstellt werden");
        } finally {
            setPdfBusy(false);
        }
    };

    return (
        <div data-testid="seller-commissions-tab">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
                <Kpi label="Provision gesamt" value={formatPrice(totals.total || 0)} testid="commission-kpi-total" />
                <Kpi label="Bestätigt" value={formatPrice(totals.confirmed || 0)} cls="text-emerald-700" testid="commission-kpi-confirmed" />
                <Kpi label="Noch zu bestätigen" value={formatPrice(totals.open_confirm || 0)} cls="text-amber-600" testid="commission-kpi-open" />
                <Kpi label="Angefordert" value={formatPrice(totals.requested || 0)} cls="text-indigo-700" testid="commission-kpi-requested" />
            </div>

            <div className="flex flex-wrap items-end gap-2 mb-5 bg-gray-50 border border-gray-200 rounded-sm p-3" data-testid="commission-abrechnung-bar">
                <div>
                    <div className="text-[10px] font-bold uppercase tracking-wider text-gray-500 mb-1">Monats-Abrechnung je Verkäufer</div>
                    <div className="flex flex-wrap items-center gap-2">
                        <select value={seller} onChange={(e) => setSeller(e.target.value)}
                            className="h-9 text-sm rounded-sm border border-gray-300 px-2 bg-white min-w-[180px]" data-testid="commission-abrechnung-seller">
                            <option value="">Verkäufer wählen …</option>
                            {sellers.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                        </select>
                        <Input type="month" value={month} onChange={(e) => setMonth(e.target.value)} className="h-9 w-40 text-sm" data-testid="commission-abrechnung-month" />
                        <Button size="sm" onClick={downloadAbrechnung} disabled={pdfBusy}
                            className="h-9 rounded-sm bg-[#111827] hover:bg-black text-white text-xs font-bold" data-testid="commission-abrechnung-pdf">
                            {pdfBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <><FileText className="w-4 h-4 mr-1" /> Abrechnung als PDF</>}
                        </Button>
                    </div>
                </div>
            </div>

            <MonthlyReportSection month={month} onMonthChange={setMonth} />

            {items.length === 0 ? (
                <div className="py-12 text-center text-gray-500 border border-dashed border-gray-300 rounded-sm" data-testid="commissions-empty">
                    <Percent className="w-8 h-8 mx-auto mb-2 text-gray-300" />
                    Noch keine Verkäufer-Provisionen. Sie entstehen automatisch im Dispo-Schritt „Zahlung &amp; Rabatt", sobald ein Verkäufer zugeordnet und Provisionen berechnet sind.
                </div>
            ) : (
                <div className="overflow-x-auto border border-gray-200 rounded-sm">
                    <table className="w-full text-sm" data-testid="commissions-table">
                        <thead>
                            <tr className="bg-gray-50 text-left text-[10px] font-bold uppercase tracking-wider text-gray-500">
                                <th className="px-3 py-2">Fahrzeug / Verkäufer</th>
                                <th className="px-3 py-2 text-right">Fahrzeug-Prov.</th>
                                <th className="px-3 py-2">Bestätigung</th>
                                <th className="px-3 py-2 text-right">Zusatzertrag-Prov.</th>
                                <th className="px-3 py-2">Bestätigung</th>
                                <th className="px-3 py-2 text-right">Bestätigt</th>
                                <th className="px-3 py-2">Auszahlung</th>
                            </tr>
                        </thead>
                        <tbody>
                            {items.map((it) => (
                                <tr key={it.listing_id} className="border-t border-gray-100 align-top" data-testid={`commission-item-${it.listing_id}`}>
                                    <td className="px-3 py-2">
                                        <div className="font-bold text-gray-800">{it.brand} {it.model || it.listing_title}</div>
                                        <div className="text-[11px] text-gray-500">{it.seller_name || <span className="text-amber-700">kein Verkäufer</span>}</div>
                                        {it.requested_amount > 0 && (
                                            <div className="text-[10px] text-indigo-700 mt-0.5">abgerufen: {formatPrice(it.requested_amount)}</div>
                                        )}
                                    </td>
                                    <td className="px-3 py-2 text-right font-mono-num font-bold">{formatPrice(it.vehicle_amount || 0)}</td>
                                    <td className="px-3 py-2">
                                        <ConfCells lid={it.listing_id} line="vehicle" conf={it.conf?.vehicle} busy={busy} onToggle={toggleConf} fully={it.vehicle_confirmed} />
                                    </td>
                                    <td className="px-3 py-2 text-right font-mono-num font-bold">{formatPrice(it.extra_amount || 0)}</td>
                                    <td className="px-3 py-2">
                                        <ConfCells lid={it.listing_id} line="extra" conf={it.conf?.extra} busy={busy} onToggle={toggleConf} fully={it.extra_confirmed} />
                                    </td>
                                    <td className="px-3 py-2 text-right font-mono-num font-black text-emerald-700">{formatPrice(it.confirmed_total || 0)}</td>
                                    <td className="px-3 py-2">
                                        <span className={`inline-block px-2 py-0.5 rounded-sm text-[10px] font-black uppercase mb-1 ${PAYOUT_CLS[it.payout_status] || PAYOUT_CLS.offen}`} data-testid={`commission-payout-badge-${it.listing_id}`}>
                                            {PAYOUT_LABELS[it.payout_status] || it.payout_status}
                                        </span>
                                        <select
                                            value={it.payout_status}
                                            disabled={busy === `${it.listing_id}-payout`}
                                            onChange={(e) => setPayout(it.listing_id, e.target.value)}
                                            className="block w-full h-8 text-xs rounded-sm border border-gray-300 px-1.5 bg-white"
                                            data-testid={`commission-payout-select-${it.listing_id}`}
                                        >
                                            <option value="offen">Offen</option>
                                            <option value="angefordert">Angefordert</option>
                                            <option value="abgerechnet">Abgerechnet</option>
                                            <option value="ausgezahlt">Ausgezahlt</option>
                                        </select>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
}

// Jul-2026 (User-Wunsch) — Monatsübersicht je Verkäufer für die Lohnabrechnung.
function MonthlyReportSection({ month, onMonthChange }) {
    const [report, setReport] = useState(null);
    const [loading, setLoading] = useState(true);
    const [expanded, setExpanded] = useState("");

    useEffect(() => {
        let alive = true;
        setLoading(true);
        api.get("/dealer/seller-commissions/monthly-report", { params: { month } })
            .then((r) => { if (alive) setReport(r.data); })
            .catch(() => { if (alive) setReport(null); })
            .finally(() => { if (alive) setLoading(false); });
        return () => { alive = false; };
    }, [month]);

    const sellers = report?.sellers || [];
    const totals = report?.totals || {};
    return (
        <div className="mb-5 border border-gray-200 rounded-sm" data-testid="commission-monthly-report">
            <div className="flex flex-wrap items-center justify-between gap-2 px-4 py-3 bg-[#111827] rounded-t-sm">
                <div className="text-white font-bold text-sm flex items-center gap-2">
                    <Users className="w-4 h-4 text-[#FFCC00]" /> Provisions-Auswertung {month} — je Verkäufer (Lohnabrechnung)
                </div>
                <Input type="month" value={month} onChange={(e) => onMonthChange(e.target.value)}
                    className="h-8 w-40 text-xs bg-white" data-testid="monthly-report-month" />
            </div>
            {loading ? (
                <div className="py-6 text-center text-gray-400 text-sm" data-testid="monthly-report-loading">
                    <Loader2 className="w-4 h-4 animate-spin inline mr-2" /> Lädt …
                </div>
            ) : sellers.length === 0 ? (
                <div className="py-6 text-center text-gray-400 text-sm" data-testid="monthly-report-empty">
                    Keine Verkäufe mit zugeordnetem Verkäufer in {month}.
                </div>
            ) : (
                <div className="overflow-x-auto">
                    <table className="w-full text-sm" data-testid="monthly-report-table">
                        <thead>
                            <tr className="bg-gray-50 text-left text-[10px] font-bold uppercase tracking-wider text-gray-500">
                                <th className="px-3 py-2">Verkäufer</th>
                                <th className="px-3 py-2 text-right">Verkäufe / Rechnungen</th>
                                <th className="px-3 py-2 text-right">Umsatz</th>
                                <th className="px-3 py-2 text-right">Provision</th>
                                <th className="px-3 py-2 text-right">davon bestätigt</th>
                                <th className="px-3 py-2" />
                            </tr>
                        </thead>
                        <tbody>
                            {sellers.map((s) => (
                                <>
                                    <tr key={s.seller_id} className="border-t border-gray-100" data-testid={`monthly-report-row-${s.seller_id}`}>
                                        <td className="px-3 py-2 font-bold text-gray-800">{s.seller_name}</td>
                                        <td className="px-3 py-2 text-right font-mono-num">{s.sales_count}</td>
                                        <td className="px-3 py-2 text-right font-mono-num">{formatPrice(s.revenue)}</td>
                                        <td className="px-3 py-2 text-right font-mono-num font-black">{formatPrice(s.commission_total)}</td>
                                        <td className="px-3 py-2 text-right font-mono-num text-emerald-700 font-bold">{formatPrice(s.confirmed_total)}</td>
                                        <td className="px-3 py-2 text-right">
                                            <button type="button"
                                                onClick={() => setExpanded(expanded === s.seller_id ? "" : s.seller_id)}
                                                className="text-[10px] font-bold uppercase tracking-wider text-indigo-700 hover:underline"
                                                data-testid={`monthly-report-toggle-${s.seller_id}`}>
                                                {expanded === s.seller_id ? "Zuklappen" : "Details"}
                                            </button>
                                        </td>
                                    </tr>
                                    {expanded === s.seller_id && (s.items || []).map((it) => (
                                        <tr key={`${s.seller_id}-${it.listing_id}`} className="border-t border-gray-50 bg-gray-50/60 text-xs" data-testid={`monthly-report-item-${it.listing_id}`}>
                                            <td className="px-3 py-1.5 pl-7 text-gray-600">
                                                {it.vehicle}
                                                {it.invoice_number && <span className="ml-2 text-[10px] text-gray-400">RE {it.invoice_number}</span>}
                                                <span className="ml-2 text-[10px] text-gray-400">{it.date}</span>
                                            </td>
                                            <td className="px-3 py-1.5 text-right text-gray-400">1</td>
                                            <td className="px-3 py-1.5 text-right font-mono-num">{formatPrice(it.revenue)}</td>
                                            <td className="px-3 py-1.5 text-right font-mono-num font-bold">{formatPrice(it.commission)}</td>
                                            <td className="px-3 py-1.5 text-right font-mono-num text-emerald-700">{formatPrice(it.confirmed)}</td>
                                            <td className="px-3 py-1.5 text-right text-[10px] uppercase text-gray-400">{it.payout_status}</td>
                                        </tr>
                                    ))}
                                </>
                            ))}
                            <tr className="border-t-2 border-gray-300 bg-gray-50 font-black" data-testid="monthly-report-totals">
                                <td className="px-3 py-2">Gesamt</td>
                                <td className="px-3 py-2 text-right font-mono-num">{totals.sales_count || 0}</td>
                                <td className="px-3 py-2 text-right font-mono-num">{formatPrice(totals.revenue || 0)}</td>
                                <td className="px-3 py-2 text-right font-mono-num">{formatPrice(totals.commission_total || 0)}</td>
                                <td className="px-3 py-2 text-right font-mono-num text-emerald-700">{formatPrice(totals.confirmed_total || 0)}</td>
                                <td />
                            </tr>
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
}

function ConfCells({ lid, line, conf, busy, onToggle, fully }) {
    return (
        <div className="flex flex-col gap-1">
            {[
                { role: "filialleiter", label: "Filialleiter" },
                { role: "buchhaltung", label: "Buchhaltung" },
            ].map((c) => (
                <label key={c.role} className="flex items-center gap-1.5 cursor-pointer select-none text-[11px] text-gray-700" data-testid={`commission-conf-${lid}-${line}-${c.role}-label`}>
                    <input
                        type="checkbox"
                        checked={Boolean(conf?.[c.role])}
                        disabled={busy === `${lid}-${line}-${c.role}`}
                        onChange={(e) => onToggle(lid, line, c.role, e.target.checked)}
                        className="w-3.5 h-3.5 accent-indigo-600"
                        data-testid={`commission-conf-${lid}-${line}-${c.role}`}
                    />
                    {c.label}
                </label>
            ))}
            {fully && <span className="inline-flex items-center gap-0.5 text-[9px] font-black uppercase text-emerald-700"><CheckCircle2 className="w-3 h-3" /> ok</span>}
        </div>
    );
}
